// 生成物一覧を持つページ側が「最新化する関数」を登録し、広告自動化マンの
// 「更新する」ボタンから呼び出せるようにするためのオプトインAPI（F23-c）。
//
// このリポジトリには React Query のようなグローバルキャッシュが無い
// （FIXES.md参照）。authenticatedContent（App.tsx）への追加は1行のみという制約が
// あり children を Provider で包めないため、useAdmanPageContext.ts と同じ
// モジュールスコープの軽量な pub/sub で実装する。
//
// useSetAdmanPageContext との違い: あちらは「値」を毎レンダー最新化して送るのが目的
// （JSON.stringifyした内容が変わったときだけ再登録）。こちらは「呼び出せる関数」を
// 登録するのが目的で、渡された関数の**中身**（クロージャ）が変わるたびに再登録すると、
// 複数ページを行き来する構成（例: ImageGenerationPageの画像/動画タブ切り替え）で
// 不要な登録の付け外しが頻発する。そのため「関数を渡しているかどうか」だけを
// 依存にして登録の有無を管理し、実際に呼び出す中身は ref 経由で常に最新を参照する。
// ページ側がタブ切り替え等で自分の登録を明示的に外したい場合は null を渡す
// （useSetAdmanPageContext の `selectedProductData ? {...} : null` と同じ流儀）。
import { useEffect, useRef, useState } from 'react';

export type AdmanRefreshHandler = () => void;

let currentRefreshHandler: AdmanRefreshHandler | null = null;
const refreshHandlerListeners = new Set<(handler: AdmanRefreshHandler | null) => void>();

const notifyRefreshHandlerListeners = (): void => {
  refreshHandlerListeners.forEach((listener) => listener(currentRefreshHandler));
};

// ページ側から呼ぶ。handler が null/undefined の間は登録しない。
export const useAdmanRefreshHandler = (handler: AdmanRefreshHandler | null | undefined): void => {
  // 呼び出す中身は常にこのrefから読む。レンダーのたびに更新するだけで、
  // effectの再実行（＝登録し直し）は起こさない。
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const isEnabled = typeof handler === 'function';

  useEffect(() => {
    if (!isEnabled) return undefined;

    const stableWrapper: AdmanRefreshHandler = () => {
      handlerRef.current?.();
    };
    currentRefreshHandler = stableWrapper;
    notifyRefreshHandlerListeners();

    return () => {
      // 離脱時は自分が登録したハンドラだけをクリアする（後から登録された別ページの
      // ハンドラを誤って消さないため）。
      if (currentRefreshHandler === stableWrapper) {
        currentRefreshHandler = null;
        notifyRefreshHandlerListeners();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);
};

// パネル側から呼ぶ。現在登録されているハンドラ（無ければnull）を返す。
export const useAdmanRegisteredRefreshHandler = (): AdmanRefreshHandler | null => {
  const [handler, setHandler] = useState<AdmanRefreshHandler | null>(() => currentRefreshHandler);

  useEffect(() => {
    // 購読を始める時点の最新値に同期する。ページ側（useAdmanRefreshHandler）の登録effectが
    // 先に走って通知（notify）済みのケースを取りこぼさないため（両方ともマウント時に1回だけ
    // 走るeffectなので、兄弟コンポーネント間の実行順に依存しないようにする）。
    setHandler(() => currentRefreshHandler);
    const listener = (value: AdmanRefreshHandler | null) => setHandler(() => value);
    refreshHandlerListeners.add(listener);
    return () => {
      refreshHandlerListeners.delete(listener);
    };
  }, []);

  return handler;
};

// テスト用: モジュールスコープの状態をリセットする。
export const __resetAdmanRefreshHandlerForTests = (): void => {
  currentRefreshHandler = null;
  refreshHandlerListeners.clear();
};
